import React from 'react';
import Card from './Card';
import LayeredCharacter from '../character/LayeredCharacter';
import { getCharacterShopOutfit } from '../../utils/characterPresets';
import { getRoomSurfaceImage } from '../../types/myHome';
import type { CharacterLayerSlot } from '../../types/character';
import { FEATURE_CHARACTER_COSMETIC } from '../../config/featureFlags';
import { PET_CONSUMABLE_CATEGORY, PET_PLAY_TICKET_CATEGORY } from '../../types/petStore';

interface StoreItem {
  id: string;
  name: string;
  description: string;
  price: number;
  itemCategory: 'usage' | 'qr' | 'seatReservation' | 'seatTakeover' | 'collectible' | 'gacha' | 'cosmetic' | 'furniture' | 'roomSurface' | typeof PET_CONSUMABLE_CATEGORY | typeof PET_PLAY_TICKET_CATEGORY;
  characterSlot?: CharacterLayerSlot;
  characterOutfitId?: string;
  roomSurfaceKey?: string;
  isActive?: boolean;
  stock: number;
  isUnlimitedStock: boolean;
  isLimited: boolean;
  salePeriod?: {
    start: Date | string;
    end: Date | string;
  };
  imageUrl?: string;
  // 추가 속성들 (선택적)
  targetType: 'class' | 'subject';
  itemType?: 'point' | 'stamp';
  createdAt?: Date;
  updatedAt?: Date;
  teacherId?: string;
  subjectId?: string;
  qrData?: string;
  qrCodeExpiry?: number;
}

interface StoreItemCardForStudentProps {
  item: StoreItem;
  isMobile?: boolean;
  onPurchase?: (item: StoreItem) => void | Promise<void>;
  canPurchase?: boolean; // 구매 가능 여부
  formatDate?: (date: Date) => string;
}

const StoreItemCardForStudent: React.FC<StoreItemCardForStudentProps> = ({
  item,
  isMobile = false,
  onPurchase,
  canPurchase = true,
  formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}.${month}.${day}`;
  }
}) => {
  const outfit = item.itemCategory === 'cosmetic' ? getCharacterShopOutfit(item.characterOutfitId) : undefined;
  const displayImage = outfit?.shopImageUrl || (item.itemCategory === 'roomSurface' ? getRoomSurfaceImage(item.roomSurfaceKey) : undefined) || item.imageUrl;
  // 데스크톱 버전 - 학생용 카드 (모바일에서도 동일한 크기 사용)
  return (
    <div style={{
      width: '243px',
      height: '477px',
      position: 'relative',
      background: 'var(--Color-background-normal, white)',
      boxShadow: '0px 0px 5px rgba(0, 0, 0, 0.05)',
      overflow: 'hidden',
      borderRadius: '12px'
    }}>
      {/* 이미지 영역 */}
      <div style={{
        width: '243px',
        height: '158px',
        left: 0,
        top: 0,
        position: 'absolute',
        background: 'var(--Color-background-sub, #F8F8F8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
      }}>
        {FEATURE_CHARACTER_COSMETIC && item.itemCategory === 'cosmetic' && item.imageUrl && item.characterSlot && !item.characterOutfitId ? (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--Color-background-sub, #F8F8F8)'
            }}
          >
            <LayeredCharacter
              scale={4}
              layers={{ [item.characterSlot]: item.imageUrl }}
            />
          </div>
        ) : displayImage ? (
          <img
            src={displayImage}
            alt={item.name}
            style={{
              width: '100%',
              height: '100%',
              objectFit: item.itemCategory === 'cosmetic' ? 'contain' : 'cover'
            }}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
              const parent = (e.target as HTMLImageElement).parentElement;
              if (parent) {
                parent.innerHTML = '<span style="font-size: 48px; color: #989898;">📦</span>';
              }
            }}
          />
        ) : (
          <span style={{
            fontSize: '48px',
            color: '#989898'
          }}>
            📦
          </span>
        )}
      </div>

      {/* 정보 영역 */}
      <div style={{
        width: '203px',
        left: '20px',
        top: '182px',
        position: 'absolute',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: '24px',
        display: 'inline-flex'
      }}>
        <div style={{
          width: '203px',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          gap: '24px',
          display: 'flex'
        }}>
          {/* 제목 및 설명 영역 */}
          <div style={{
            alignSelf: 'stretch',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            gap: '20px',
            display: 'flex'
          }}>
            {/* 제목 및 설명 */}
            <div style={{
              alignSelf: 'stretch',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              gap: '8px',
              display: 'flex'
            }}>
              <div style={{
                alignSelf: 'stretch',
                height: '27px',
                color: 'var(--Color-text-normal, #161616)',
                fontSize: '18px',
                fontFamily: 'pretendard',
                fontWeight: '600',
                lineHeight: '27px',
                wordWrap: 'break-word',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {item.name}
              </div>
              <div style={{
                alignSelf: 'stretch',
                color: 'var(--Color-text-assistive-strong, #4C4C4C)',
                fontSize: '13px',
                fontFamily: 'pretendard',
                fontWeight: '500',
                lineHeight: '19.50px',
                wordWrap: 'break-word',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical'
              }}>
                {item.description || '설명이 없습니다...'}
              </div>
            </div>

            {/* 기간 및 유형 */}
            <div style={{
              alignSelf: 'stretch',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              gap: '8px',
              display: 'flex'
            }}>
              {/* 기간 */}
              <div style={{
                alignSelf: 'stretch',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                gap: '8px',
                display: 'inline-flex'
              }}>
                <div style={{
                  width: '51px',
                  color: 'var(--Color-text-assistive, #989898)',
                  fontSize: '13px',
                  fontFamily: 'pretendard',
                  fontWeight: '500',
                  lineHeight: '19.50px',
                  wordWrap: 'break-word'
                }}>
                  기간
                </div>
                <div style={{
                  textAlign: 'right',
                  color: 'var(--Color-text-normal, #161616)',
                  fontSize: '13px',
                  fontFamily: 'pretendard',
                  fontWeight: '500',
                  lineHeight: '19.50px',
                  wordWrap: 'break-word',
                  flex: 1
                }}>
                  {item.salePeriod 
                    ? `${formatDate(item.salePeriod.start instanceof Date ? item.salePeriod.start : new Date(item.salePeriod.start))}~${formatDate(item.salePeriod.end instanceof Date ? item.salePeriod.end : new Date(item.salePeriod.end))}`
                    : '제한 없음'
                  }
                </div>
              </div>

              {/* 유형 */}
              <div style={{
                alignSelf: 'stretch',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                gap: '8px',
                display: 'inline-flex'
              }}>
                <div style={{
                  width: '51px',
                  color: 'var(--Color-text-assistive, #989898)',
                  fontSize: '13px',
                  fontFamily: 'pretendard',
                  fontWeight: '500',
                  lineHeight: '19.50px',
                  wordWrap: 'break-word'
                }}>
                  유형
                </div>
                <div style={{
                  color: 'var(--Color-text-normal, #161616)',
                  fontSize: '13px',
                  fontFamily: 'pretendard',
                  fontWeight: '500',
                  lineHeight: '19.50px',
                  wordWrap: 'break-word'
                }}>
                  {item.itemCategory === 'qr' ? 'QR 코드' :
                   item.itemCategory === 'seatReservation' ? '좌석 예약권' :
                   item.itemCategory === 'seatTakeover' ? '좌석 강탈권' :
                   item.itemCategory === 'collectible' ? '소장용' :
                   item.itemCategory === 'gacha' ? '뽑기' :
                   item.itemCategory === PET_CONSUMABLE_CATEGORY ? '캐릭터 간식' :
                   item.itemCategory === PET_PLAY_TICKET_CATEGORY ? '놀아주기 이용권' :
                   item.itemCategory === 'cosmetic' ? (item.characterOutfitId === 'orange-star-jacket' ? 'SD 상의' : '캐릭터 복장') : '사용권'}
                </div>
              </div>
            </div>
          </div>

          {/* 금액 및 재고 영역 */}
          <div style={{
            alignSelf: 'stretch',
            height: '56px',
            justifyContent: 'flex-start',
            alignItems: 'flex-end',
            gap: '41px',
            display: 'inline-flex'
          }}>
            <div style={{
              width: '203px',
              alignSelf: 'stretch',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: '25px',
              display: 'flex'
            }}>
              {/* 금액 */}
              <div style={{
                flexShrink: 0,
                alignSelf: 'stretch',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                gap: '4px',
                display: 'inline-flex'
              }}>
                <div style={{
                  alignSelf: 'stretch',
                  color: 'var(--Color-text-assistive, #989898)',
                  fontSize: '12px',
                  fontFamily: 'pretendard',
                  fontWeight: '500',
                  lineHeight: '18px',
                  wordWrap: 'break-word'
                }}>
                  금액
                </div>
                <div style={{
                  alignSelf: 'stretch',
                  height: '31px',
                  color: 'var(--Color-text-brand-color, #FF6F0F)',
                  fontSize: '24px',
                  fontFamily: 'pretendard',
                  fontWeight: '600',
                  lineHeight: '36px',
                  whiteSpace: 'nowrap'
                }}>
                  {item.price}
                </div>
              </div>

              {/* 재고 */}
              <div style={{
                width: '42px',
                alignSelf: 'stretch',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                gap: '4px',
                display: 'inline-flex'
              }}>
                <div style={{
                  alignSelf: 'stretch',
                  color: 'var(--Color-text-assistive, #989898)',
                  fontSize: '12px',
                  fontFamily: 'pretendard',
                  fontWeight: '500',
                  lineHeight: '18px',
                  wordWrap: 'break-word'
                }}>
                  재고
                </div>
                <div style={{
                  alignSelf: 'stretch',
                  height: '31px',
                  color: 'var(--Color-text-brand-color, #FF6F0F)',
                  fontSize: '24px',
                  fontFamily: 'pretendard',
                  fontWeight: '600',
                  lineHeight: '36px',
                  wordWrap: 'break-word'
                }}>
                  {item.isUnlimitedStock ? '없음' : item.stock}
                </div>
              </div>
            </div>
          </div>

          {/* 구매하기 버튼 */}
          {onPurchase && (
            <div style={{
              alignSelf: 'stretch',
              height: '32px',
              paddingLeft: '12px',
              paddingRight: '12px',
              paddingTop: '8px',
              paddingBottom: '8px',
              background: canPurchase ? 'var(--Color-fill-normal, #293044)' : '#E9E9E9',
              borderRadius: '8px',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '4px',
              display: 'inline-flex',
              cursor: canPurchase ? 'pointer' : 'not-allowed'
            }}
            onClick={() => canPurchase && onPurchase(item)}
            >
              <div style={{
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: '4px',
                display: 'flex'
              }}>
                <div style={{
                  color: canPurchase ? 'var(--Color-text-inverse, white)' : '#989898',
                  fontSize: '14px',
                  fontFamily: 'pretendard',
                  fontWeight: '400',
                  lineHeight: '21px',
                  wordWrap: 'break-word'
                }}>
                  구매하기
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 상단 태그 영역 */}
      <div style={{
        width: '203px',
        left: '20px',
        top: '16px',
        position: 'absolute',
        justifyContent: 'space-between',
        alignItems: 'center',
        display: 'inline-flex'
      }}>
        <div style={{
          justifyContent: 'flex-start',
          alignItems: 'center',
          display: 'flex'
        }}>
          {item.isLimited && (
            <div style={{
              paddingLeft: '12px',
              paddingRight: '12px',
              paddingTop: '2px',
              paddingBottom: '2px',
              background: '#FFD2B5',
              borderRadius: '8px',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '4px',
              display: 'flex'
            }}>
              <div style={{
                color: 'var(--Color-text-brand-color, #FF6F0F)',
                fontSize: '12px',
                fontFamily: 'pretendard',
                fontWeight: '500',
                lineHeight: '18px',
                wordWrap: 'break-word'
              }}>
                1회 구매
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StoreItemCardForStudent;
